import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useMemo } from 'react';
import { useOptionsByPathQuery } from '../../../../hooks/useOptionsByPathQuery.ts';

const filterInstancesByMarkets = (instances, market) =>
  market ? instances.filter(({ market_operator_id }) => market_operator_id === market) : instances;

export const useFetchInventoryInstances: TargetingProviderConfig['useFetchInventoryInstances'] = (
  marketId: null | string = null
) => {
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const isUserAllowed =
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.ROOT_WORKAS_ANYINSTANCE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.MARKET_OPERATOR_SEARCH));

  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();

  const { data: { optionsByPath = [] } = {}, loading } = useOptionsByPathQuery({
    skip: !(isUserAllowed && isBefInstance),
    variables: { path: 'deal.package.targeting.content.instance' },
  });

  const list = useMemo(() => {
    if (!optionsByPath) {
      return [];
    }

    const options = optionsByPath.map(option =>
      option
        ? {
            id: option.extra.uid,
            name: option.name,
            ...option.extra,
          }
        : []
    );

    return filterInstancesByMarkets(options, marketId);
  }, [optionsByPath, marketId]);

  return { instances: list, loading };
};
